import * as React from 'react';
import { styled } from '@glitz/react';
import { bareListDecorator } from 'webapp/decorators/list';
import { createMinLargeViewportDecorator } from 'webapp/viewport';
import { CheckboxAlign, CheckboxField } from 'webapp/components/form/checkbox-field';
import { useFacetValues } from 'webapp/seo-subpages';
import { type FacetValueWithChildren } from './Facet';
import { ExpandableBox } from 'webapp/components/expandable-box';
import { translate } from '@avensia/excite';
import { NnTestIdProps } from 'webapp/shared/test-ids';

type PropType = {
  facetList: Excite.Esales.FacetApiModel;
  onClickFaceValue: (
    value: Excite.Esales.FacetValueApiModel | Excite.Esales.FacetValueApiModel[],
    checked: boolean,
  ) => void;
  compact?: boolean;
};

const RenderFacetValue = ({
  value,
  compact,
  onClickFaceValue,
}: {
  value: FacetValueWithChildren;
  compact: boolean | undefined;
  onClickFaceValue: (
    value: Excite.Esales.FacetValueApiModel | Excite.Esales.FacetValueApiModel[],
    checked: boolean,
  ) => void;
}) => {
  if (value.children && value.children.length > 0) {
    // Parent facet with children
    const handleToggleAll = (checked: boolean) => {
      onClickFaceValue(value.children!, checked);
    };

    return (
      <ExpandableBox
        key={value.text}
        header={value.text}
        id={value.text}
        facetHierarchy
        compactFacetHierarchy={compact}
        contentPadding={false}
      >
        <ExpandableListItem
          css={compact && expandableCompactDecorator}
          {...NnTestIdProps.FacetHeaderExpandableAllItems}
        >
          <FacetValue
            checked={value.children.every(child => child.selected)} // All children selected
            onChange={e => handleToggleAll(e.currentTarget.checked)}
            checkboxAlign={CheckboxAlign.Right}
          >
            <Text css={toggleAllTextDecorator}>
              {translate(t => t.productListing.toggleAllFacetsText, {
                ['facet']: value.displayText!,
              })}
            </Text>
            <Count {...NnTestIdProps.FacetHeaderItemCount}>{value.count}</Count>
          </FacetValue>
        </ExpandableListItem>
        {value.children.map(child => (
          <ExpandableListItem
            key={child.text}
            css={compact && expandableCompactDecorator}
            {...NnTestIdProps.FacetHeaderExpandableItem}
          >
            <FacetValue
              checked={child.selected}
              onChange={e => onClickFaceValue(child, e.currentTarget.checked)}
              checkboxAlign={CheckboxAlign.Right}
            >
              <Text>{child.displayText}</Text>
              <Count {...NnTestIdProps.FacetHeaderItemCount}>{child.count}</Count>
            </FacetValue>
          </ExpandableListItem>
        ))}
      </ExpandableBox>
    );
  }

  // Single facet value (no children)
  return (
    <ListItem key={value.text} css={compact && compctDecorator} {...NnTestIdProps.FacetHeaderItem}>
      <FacetValue
        checked={value.selected}
        onChange={e => onClickFaceValue(value, e.currentTarget.checked)}
        checkboxAlign={CheckboxAlign.Right}
      >
        <Text>{value.text}</Text>
        <Count {...NnTestIdProps.FacetHeaderItemCount}>{value.count}</Count>
      </FacetValue>
    </ListItem>
  );
};

export function FacetValues({ facetList, onClickFaceValue, compact }: PropType) {
  const values = useFacetValues(facetList);

  return (
    <FacetValueList css={compact && paddingDecorator}>
      {values.map(([value]) => (
        <RenderFacetValue key={value.text} value={value} compact={compact} onClickFaceValue={onClickFaceValue} />
      ))}
    </FacetValueList>
  );
}

const FacetValueList = styled.ul(bareListDecorator, {
  display: 'flex',
  flexDirection: 'column',
});

const paddingDecorator = styled({
  padding: { y: t => t.sharedSpacing.xs },
});

const ListItem = styled.li({
  borderBottom: { style: 'solid', width: 'thin', color: t => t.facets?.borderColor },
  padding: { xy: t => t.sharedSpacing.m, left: t => t.sharedSpacing.l },
});

const ExpandableListItem = styled(ListItem, { paddingLeft: t => t.sharedSpacing.l * 2 });

const compctDecorator = styled({
  borderBottom: { width: 0 },
  padding: { x: t => t.sharedSpacing.m, y: t => t.sharedSpacing.xs },
});

const expandableCompactDecorator = styled(compctDecorator, { padding: { left: t => t.sharedSpacing.xl } });

const FacetValue = styled(CheckboxField, { width: '100%', fontSize: t => t.fontSize.xxs });

const Count = styled.span({
  fontWeight: 'normal',
  color: t => t.facets?.count?.color,
  fontSize: t => t.facets?.count?.fontSize,
});

const toggleAllTextDecorator = styled({ fontWeight: t => t.fontWeight?.bold });

const Text = styled.span(
  createMinLargeViewportDecorator({
    fontSize: t => t.facets?.facet?.fontSize,
  }),
  {
    flexGrow: 1,
  },
);
